import { useEffect, useState } from 'react';
import { getSpotDashboard } from '../../api/spots';
import type { SpotMineResponse, SpotDashboardResponse } from '../../types/api';
import { SpotMiniMap } from './SpotMiniMap';
import { LatestCatchesList } from './LatestCatchesList';
import { SpeciesBreakdownChart } from './SpeciesBreakdownChart';
import { CatchesOverTimeChart } from './CatchesOverTimeChart';
import { WeatherOutlookCard } from './WeatherOutlookCard';

interface Props {
  spot: SpotMineResponse;
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm flex flex-col gap-1">
      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-2xl font-bold text-slate-900 dark:text-slate-50">{value}</span>
    </div>
  );
}

export function SpotDashboard({ spot }: Props) {
  const [data, setData] = useState<SpotDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    getSpotDashboard(spot.id)
      .then(setData)
      .catch(() => setError('Failed to load spot data.'))
      .finally(() => setLoading(false));
  }, [spot.id]);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-56 rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-800" />)}
          </div>
        </div>
        <div className="h-40 rounded-xl bg-slate-100 dark:bg-slate-800" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-48 rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="h-48 rounded-xl bg-slate-100 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  if (error) return <p className="text-red-500 text-sm">{error}</p>;
  if (!data) return null;

  const biggestFishLabel = data.biggestFish
    ? `${data.biggestFish.fishTypeName}${data.biggestFish.lengthInches ? ` — ${data.biggestFish.lengthInches}″` : ''}`
    : '—';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SpotMiniMap latitude={Number(spot.latitude)} longitude={Number(spot.longitude)} name={spot.name} />
        <div className="grid grid-cols-2 gap-3">
          <StatTile label="Total Catches" value={data.totalCatches} />
          <StatTile label="Species" value={data.speciesBreakdown.length} />
          <div className="col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wide block mb-1">Biggest Fish</span>
            {data.biggestFish ? (
              <div>
                <span className="text-base font-bold text-slate-900 dark:text-slate-50">{biggestFishLabel}</span>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">by {data.biggestFish.anglerDisplayName}</p>
              </div>
            ) : (
              <span className="text-slate-400 dark:text-slate-500 text-sm">No measured catches yet</span>
            )}
          </div>
        </div>
      </div>

      <LatestCatchesList catches={data.latestCatches} />

      <WeatherOutlookCard spotId={spot.id} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SpeciesBreakdownChart data={data.speciesBreakdown} />
        <CatchesOverTimeChart monthlyCounts={data.monthlyCounts} yearlyCounts={data.yearlyCounts} />
      </div>
    </div>
  );
}
